import {
  Calendar as RBCalendar,
  SlotInfo,
  ToolbarProps,
  View,
  dayjsLocalizer,
} from "react-big-calendar";
import widthDragAndDrop, {
  EventInteractionArgs,
} from "react-big-calendar/lib/addons/dragAndDrop";
import dayjs from "dayjs";
import "react-big-calendar/lib/css/react-big-calendar.css";
import "./override.css";
import {Stack} from "@mui/material";
import {useEffect} from "react";
import {Task, TaskEvent} from "@core/model";
import {api, taskApi} from "@core/api";
import {skipToken} from "@reduxjs/toolkit/query";
import {TaskIcon} from "@components/icons";
import {DeleteDialog, EditDialog, LoadingView} from "@components";
import {EditDialogProps} from "@components/EditDialog";
import {taskForm, taskRecurringForm} from "./form";
import {DeleteDialogProps} from "@components/DeleteDialog";
import {canManageOwned, isManager, translate} from "@util";
import {CalendarDetail} from "./detail";
import {useThemeContext} from "@theme";
import {useDownBreakpoint, useEntityDialog, useIsTouch} from "@hooks";
import {
  getMessages,
  setCalendarVariables,
  unsetCalendarVariables,
} from "./util";
import {CalendarEvent} from "./Event";
import {CalendarToolbar} from "./toolbar";
import {Day} from "./Day";
import {ShowMore} from "./ShowMore";
import {useCalendarContext} from ".";
import {AgendaView} from "./agenda/View";
import {DialogAction} from "@hooks/useCrudDialogs";
import {useSelector} from "react-redux";
import {Selectors} from "@core/store/selectors";

const localizer = dayjsLocalizer(dayjs);
const DnDCalendar = widthDragAndDrop<TaskEvent>(RBCalendar);
export type calendarMode = "simple" | "advanced";
export type CalendarToolbarProp = ToolbarProps<TaskEvent>;

const Calendar = ({
  view,
  taskId,
  onView,
}: {
  view: View;
  taskId?: string;
  onView: (view: View) => void;
}) => {
  const {theme} = useThemeContext();
  const isTouch = useIsTouch();
  const isDownMd = useDownBreakpoint("md");
  const user = useSelector(Selectors.user);
  // admin e project manager creano i task, il project manager modifica solo i propri
  const canCreate = isManager(user?.role);
  const canManage = (event: TaskEvent) => canManageOwned(user, event);

  const updateCallback = (data: Partial<Task>, action: DialogAction) => {
    if (action === "delete" && selected?.id === data.id) {
      setSelected(undefined);
    } else if (action === "add") {
      setSelected(data as Task);
    }
  };

  const {
    dialog,
    isLoading: isEditLoading,
    handleEditClick,
    setDialog,
  } = useEntityDialog<Task>({
    entity: "task",
    EntityIcon: TaskIcon,
    form: taskForm,
    selectValues: {
      recurrenceRule: [
        {id: "FREQ=DAILY;INTERVAL=1", name: translate("entity.task.daily")},
        {id: "FREQ=WEEKLY;INTERVAL=1", name: translate("entity.task.weekly")},
        {id: "FREQ=MONTHLY;INTERVAL=1", name: translate("entity.task.monthly")},
      ],
    },
    invalidateTags: [{type: "task", id: "range"}],
    updateCallback,
    useAddEntity: taskApi.useAddEntity,
    useUpdateEntity: taskApi.useUpdateEntity,
    useDeleteEntity: taskApi.useDeleteEntity,
  });

  const {
    tasks,
    setTasks,
    selected,
    setSelected,
    taskEvents,
    range,
    setRange,
    date,
    setDate,
  } = useCalendarContext();

  useEffect(() => {
    if (!range) {
      switch (view) {
        case "agenda": {
          const now = dayjs();
          const from = now.startOf("month").unix() * 1000;
          const to = now.endOf("month").unix() * 1000;
          setRange({from, to});
          break;
        }
        case "month": {
          const now = dayjs();
          // first and last day visible in the month view (full weeks)
          const from = now.startOf("month").startOf("week").unix() * 1000;
          const to = now.endOf("month").endOf("week").unix() * 1000;
          setRange({from, to});
          break;
        }
        case "week": {
          const now = dayjs();
          const from = now.startOf("week").unix() * 1000;
          const to = now.endOf("week").unix() * 1000;
          setRange({from, to});
          break;
        }
        case "day": {
          const now = dayjs();
          const from = now.startOf("day").unix() * 1000;
          const to = now.endOf("day").unix() * 1000;
          setRange({from, to});
          break;
        }
      }
    }
  }, [range]);

  const {
    data: taskDetail,
    isLoading: isTaskDetailLoading,
    isFetching: isTaskDetailFetching,
  } = taskApi.useFindEntityById(taskId ?? skipToken);
  const [update, {isLoading: isUpdateLoading}] = taskApi.useUpdateEntity();
  const {
    data,
    isLoading: isTasksLoading,
    isFetching: isTasksFetching,
  } = api.useFetchTasksByRangeQuery(range ?? skipToken);

  // Imposta le variabili CSS basate sul theme per i colori
  useEffect(() => {
    setCalendarVariables(theme);
    return () => {
      unsetCalendarVariables();
    };
  }, [theme]);

  useEffect(() => {
    setTasks(
      data?.map(t => ({...t, recurring: t.recurrenceRule !== null})) ?? [],
    );
  }, [data]);

  useEffect(() => {
    if (taskDetail) {
      const start = dayjs(taskDetail.start)
        .startOf("month")
        .startOf("week")
        .toDate();
      const end = dayjs(taskDetail.end).endOf("month").endOf("week").toDate();
      setDate(new Date(taskDetail.start));
      setRange({from: start.getTime(), to: end.getTime()});
      setSelected(taskDetail);
    }
  }, [taskDetail]);

  const handleSelectEvent = (taskEvent: TaskEvent) => {
    const task = tasks.find(t => t.id === taskEvent.id);
    setSelected(task);
  };

  const handleRangeChange = (range: Date[] | {start: Date; end: Date}) => {
    let start: Date;
    let end: Date;

    if (!Array.isArray(range)) {
      const r = range as {start: Date; end: Date};
      start = dayjs(r.start).startOf("day").toDate();
      end = dayjs(r.end).endOf("day").toDate();
    } else {
      const r = range as Date[];
      start = dayjs(r[0]).startOf("day").toDate();
      end = dayjs(r[r.length - 1])
        .endOf("day")
        .toDate();
    }

    setRange({from: start.getTime(), to: end.getTime()});
  };

  const handleSelectSlot = (slotInfo: SlotInfo) => {
    if (!canCreate) return;
    const start = dayjs(slotInfo.start);

    const selectedData: Partial<Task> = {
      start: start.unix() * 1000,
      end: start.add(1, "hour").unix() * 1000,
      assigneeId: user?.id,
    };

    handleEditClick("add", selectedData);
  };

  const handleDragResize = (payload: EventInteractionArgs<TaskEvent>) => {
    const {event, start, end} = payload;
    const id = event.id;
    const t = tasks.find(t => t.id === id);
    if (!t) return; // Safety check

    const updatedTask: Task = {
      ...t,
      start: (start as Date).getTime(),
      end: (end as Date).getTime(),
    };

    // Update locale immediato
    setTasks(prevTasks =>
      prevTasks.map(task => (task.id === id ? updatedTask : task)),
    );

    // Sync col server (come prima)
    update(updatedTask)
      .unwrap()
      .catch(error => {
        console.error("Errore update task:", error);
        setTasks(prevTasks =>
          prevTasks.map(task => (task.id === id ? t : task)),
        );
      });
  };

  /**
   * Assicura che quando un task viene convertito in ricorrente o viceversa, i campi specifici per la ricorrenza vengano gestiti correttamente.
   * Quando il campo "recurring" viene modificato, questa funzione aggiorna il form e i dati del task di conseguenza:
   * - Se "recurring" è true, imposta il form su taskRecurringForm e resetta le regole di ricorrenza.
   * - Se "recurring" è false, imposta il form su taskForm e resetta le regole di ricorrenza.
   *
   * @param field Il campo del task da aggiornare (es. "recurring")
   * @param value Il nuovo valore del campo (es. true/false per "recurring")
   */
  const handleRecurringFieldChange = (field: string, value: boolean) => {
    if (field === "recurring") {
      setDialog(old => {
        const data: Partial<Task> = {...old.data};
        data.recurring = value;
        data.recurrenceEnd = null;
        data.recurrenceRule = null;

        return {
          ...old,
          form: value ? taskRecurringForm : taskForm,
          data: {...old.data, ...data},
        };
      });
    }
  };

  const messages = getMessages();

  const loading = isTasksLoading || isTaskDetailLoading;
  const fetching =
    isTasksFetching || isUpdateLoading || isEditLoading || isTaskDetailFetching;

  return (
    <>
      <LoadingView loading={loading || fetching} sx={{zIndex: 1}} />

      <Stack flex={"1 1 0"} direction={"row"} minHeight={0}>
        <DnDCalendar
          key={"calendar"}
          className={`rbc ${isTouch ? "rbc-touch" : ""} rbc-${view}`}
          selectable={canCreate}
          events={taskEvents}
          messages={messages}
          date={date}
          onNavigate={setDate}
          views={{
            day: true,
            week: true,
            month: true,
            agenda: AgendaView,
          }}
          components={{
            header: ({date}) => <Day date={date} view={view} />,
            event: props => <CalendarEvent {...props} />,
            toolbar: props => (
              <CalendarToolbar {...props} disabled={loading || fetching} />
            ),
            showMore: ({remainingEvents, slotDate}) => (
              <ShowMore
                events={remainingEvents}
                date={slotDate}
                onEventClick={handleSelectEvent}
              />
            ),
          }}
          localizer={localizer}
          view={view}
          draggableAccessor={canManage}
          resizableAccessor={canManage}
          onView={onView}
          onSelectEvent={handleSelectEvent}
          onRangeChange={handleRangeChange}
          onSelectSlot={handleSelectSlot}
          onEventDrop={handleDragResize}
          onEventResize={handleDragResize}
          popup
        />

        <CalendarDetail
          task={selected}
          dialog={isDownMd}
          onClose={() => setSelected(undefined)}
          onDelete={t => handleEditClick("delete", t)}
          onEdit={t =>
            handleEditClick(
              "edit",
              t,
              t.recurring ? taskRecurringForm : taskForm,
            )
          }
        />
      </Stack>

      {dialog?.action === "delete" && (
        <DeleteDialog
          {...(dialog as DeleteDialogProps<Task>)}
          isLoading={isEditLoading}
        />
      )}
      {(dialog?.action === "add" || dialog?.action === "edit") && (
        <EditDialog
          {...(dialog as EditDialogProps<Task>)}
          entity="task"
          isLoading={isEditLoading}
          onFieldChange={handleRecurringFieldChange}
        />
      )}
    </>
  );
};

export {Calendar};
